import * as React from 'react'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { Text } from '@amzn/dvui/dist/Text'
import { Modal } from '@amzn/dvui/dist/Modal'
import * as st from './playback.module.scss'
import { tryCatch } from '../../utils/tryCatch'
import { RecordRefTag } from '@utils'
import { getPlaybackClickstreamRef } from '../../utils/getPlaybackClickstreamRef'
import { getPlaybackProgress, getAppPlaybackProgress } from '../../utils/getPlaybackProgress'
import { PlayBox, SquarePlayButton } from '../playButton/PlayButton'
import {
  MaybeSpaContextProps,
  withMaybeSpaContext,
} from '@shared/SPA/SpaContext'
import { PlayerContext } from '@shared/player/integration/playbackArea'
import { triggerPlayback as triggerWindowsPlayback } from '@shared/SPA/triggers'
import {
  BeforePlayModal,
  shouldOpenBeforePlayModal
} from '@shared/SPA/downloadButton/BeforePlayModal'
import { AtfTreatmentProps } from '../../reducers/features'
import { isTreatment } from '../../utils/utils'
import * as utilityClasses from '../utilityClasses.module.scss'
import * as cn from 'classnames'

export interface IPlaybackProps {
  playbackData?: WpsRuntime.atv.wps.PlaybackActionDataProps
  isRetail?: boolean
  isStartOver?: boolean
  duration?: number
}

type PlaybackWithSpaContextProps = IPlaybackProps & MaybeSpaContextProps & AtfTreatmentProps

type Handler = undefined | ((e: React.MouseEvent) => void)

export const Playback = tryCatch(
  ({
     playbackData,
     isRetail,
     duration,
     isStartOver,
     hasDownloadCompleted,
     getDownload,
     downloads,
     strings,
     atfTreatment
   }: PlaybackWithSpaContextProps) => {
    if (!playbackData) {
      return null
    }

    const playbackTitleId = playbackData.playbackID
    const downloadCompleted =
      hasDownloadCompleted && hasDownloadCompleted(playbackTitleId)

    const downloadStatus = downloadCompleted && getDownload(playbackTitleId)
    const beforePlayModal = React.createRef<Modal>()
    const triggerOfflinePlayback = (e: React.MouseEvent) => {
      if (downloadStatus.showModalBeforePlay && beforePlayModal && shouldOpenBeforePlayModal(downloadStatus.showModalBeforePlay.Type)) {
         beforePlayModal.current.open()
      } else {
        triggerWindowsPlayback(
          playbackTitleId,
          downloadStatus.download.DownloadId
        )
      }
      e.preventDefault()
    }

    const playbackContent = {
      contentConfig: {
        asin: playbackTitleId,
        trailer: false,
        position: playbackData.resumeTime,
        ref: getPlaybackClickstreamRef(
          playbackData.contentType,
          playbackData.entitlementType
        ),
        videoType: playbackData.videoMaterialType,
        useFullView: true,
      },
      signInUrl: playbackData.signInURL,
      playbackUrl: playbackData.playbackURL,
      fallbackUrl: playbackData.fallbackURL,
    }

    return (
      <PlayerContext.Consumer>
        {({ onPlay }) => {
          const triggerPlayback: Handler = (e) => {
            onPlay(playbackContent)
            e.preventDefault()
          }

          const handler = downloadCompleted
            ? triggerOfflinePlayback
            : triggerPlayback
          //TODO(PVWinApp-1539): Do not always use app playback progress; decide if it should be used based on boolean flag from SPAContextProvider
          const progress = Math.max(getAppPlaybackProgress(downloads, playbackTitleId, duration), getPlaybackProgress(playbackData.progress))

          return (
            <>
              <RecordRefTag
                refTag='atv_dp_pb_main'
                className={`${utilityClasses.fullWidthPhablet} dv-dp-node-playback`}>
                <div className={st.playback}>
                  {(isRetail || isTreatment(atfTreatment, 'T1', 'T2', 'T3')) ? (
                    <SquarePlayButton
                      href={playbackData.playbackURL}
                      onClick={handler}
                      progress={progress}
                      forceDoubleHeight={true}
                      isStartOver={isStartOver}
                      {...(isTreatment(atfTreatment, 'T1', 'T3') && { shape: 'pill' })}
                      {...(isTreatment(atfTreatment, 'T1', 'T2', 'T3') && { className: cn(utilityClasses.fullWidthPhablet, st.playButton) })}
                      atfTreatment={atfTreatment}>
                      <Text tag='span' string={playbackData.label} />
                    </SquarePlayButton>
                  ) : (
                    <PlayBox
                      href={playbackData.playbackURL}
                      onClick={handler}
                      progress={progress}
                      isLarge={true}
                      isStartOver={isStartOver}>
                      <Text tag='span' string={playbackData.label} />
                    </PlayBox>
                  )}
                </div>
              </RecordRefTag>
              {downloadStatus && downloadStatus.showModalBeforePlay && (
                <BeforePlayModal
                  showModalBeforePlay={downloadStatus.showModalBeforePlay}
                  strings={strings}
                  downloadStatus={downloadStatus.download}
                  modalRef={beforePlayModal}
                />
              )}
            </>
          )
        }}
      </PlayerContext.Consumer>
    )
  }
)

export const ActionBoxPlayback = withMaybeSpaContext(Playback)